import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Sun, Moon } from 'lucide-react';
import { Slide0 } from '../components/slides/Slide0';
import { PilotSlide1 } from '../components/slides/PilotSlide1';
import { PilotSlide2A } from '../components/slides/PilotSlide2A';
import { PilotSlide2B } from '../components/slides/PilotSlide2B';
import { PilotSlide2C } from '../components/slides/PilotSlide2C';
import { PilotSlide2D } from '../components/slides/PilotSlide2D';
import { PilotSlide2E } from '../components/slides/PilotSlide2E';
import { PilotSlide2F } from '../components/slides/PilotSlide2F';
import { PilotSlide3 } from '../components/slides/PilotSlide3';
import { PilotSlide4 } from '../components/slides/PilotSlide4';
import { PilotSlide5 } from '../components/slides/PilotSlide5';
import { Slide1 } from '../components/slides/Slide1';
import { Slide3 } from '../components/slides/Slide3';
import { Slide4 } from '../components/slides/Slide4';
import { Slide5 } from '../components/slides/Slide5';
import { NextStepsSlide } from '../components/slides/NextStepsSlide';
import { FoundingGroupsSlide } from '../components/slides/FoundingGroupsSlide';
import { Slide6 } from '../components/slides/Slide6';
const slides = [Slide0, PilotSlide1, PilotSlide2B, PilotSlide2C, PilotSlide2D, PilotSlide2E, PilotSlide2F, PilotSlide2A, Slide4, PilotSlide3, PilotSlide4, FoundingGroupsSlide, PilotSlide5, Slide1, Slide3, Slide4, Slide5, NextStepsSlide, FoundingGroupsSlide, Slide6];
export function PitchDeck() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(0);
  const [isDark, setIsDark] = useState(false);
  const paginate = (newDirection: number) => {
    const nextSlide = currentSlide + newDirection;
    if (nextSlide >= 0 && nextSlide < slides.length) {
      setDirection(newDirection);
      setCurrentSlide(nextSlide);
    }
  };
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        paginate(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        paginate(-1);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentSlide]);
  const CurrentSlideComponent = slides[currentSlide];
  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 1000 : -1000,
      opacity: 0
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      zIndex: 0,
      x: direction < 0 ? 1000 : -1000,
      opacity: 0
    })
  };
  return <div className={`w-full h-screen overflow-hidden relative font-sans transition-colors duration-300 ${isDark ? 'bg-[#1D3C58] text-white selection:bg-heliona-mint/30' : 'bg-heliona-cream text-heliona-charcoal selection:bg-heliona-mint/30'}`} data-id="element-978">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-979">
        {isDark ? <>
            <div className="absolute top-[-20%] right-[-10%] w-[800px] h-[800px] bg-heliona-mint/5 rounded-full blur-3xl" data-id="element-980" />
            <div className="absolute bottom-[-20%] left-[-10%] w-[600px] h-[600px] bg-heliona-mint/5 rounded-full blur-3xl" data-id="element-981" />
          </> : <>
            <div className="absolute top-[-20%] right-[-10%] w-[800px] h-[800px] bg-heliona-mint/5 rounded-full blur-3xl" data-id="element-982" />
            <div className="absolute bottom-[-20%] left-[-10%] w-[600px] h-[600px] bg-heliona-pastel-blue/30 rounded-full blur-3xl" data-id="element-983" />
          </>}
      </div>

      {/* Header - Show on content slides (not cover, transition, or CTA) */}
      {currentSlide > 0 && currentSlide !== 6 && currentSlide < slides.length - 1 && <header className="absolute top-0 left-0 w-full p-8 flex justify-between items-center z-20" data-id="element-984">
            {/* Logo - show different logos for light/dark mode */}
            <div className="flex items-center" data-id="element-985">
              <img src={isDark ? 'https://cdn.magicpatterns.com/uploads/siQDa69uZkA61S2PiBLFML/Heliona_Logo_-_Dark.png' : 'https://cdn.magicpatterns.com/uploads/gsWfp7Yqa2Z6SBgP3SbdyL/HELIONA_transparent.png'} alt="Heliona Logo" className="h-8 sm:h-10" data-id="element-986" />
            </div>
            <div className={`text-sm font-medium uppercase tracking-widest ${isDark ? 'text-white/70' : 'text-heliona-text-gray'}`} data-id="element-987">
              Confidential
            </div>
          </header>}

      {/* Theme Toggle Pill Switch with Animated Position */}
      <motion.div className="absolute z-30" animate={{
      top: currentSlide === 0 ? '2rem' : 'auto',
      bottom: currentSlide === 0 ? 'auto' : '2rem',
      right: '2rem'
    }} transition={{
      type: 'spring',
      stiffness: 300,
      damping: 30,
      duration: 0.5
    }} data-id="element-988">
        <button onClick={() => setIsDark(!isDark)} className={`relative flex items-center gap-0.5 px-0.5 py-0.5 rounded-full transition-all duration-300 ${isDark ? 'bg-heliona-navy/80 border border-white/20' : 'bg-white/80 border border-heliona-charcoal/20'} backdrop-blur-sm shadow-lg hover:shadow-xl`} aria-label="Toggle theme" data-id="element-989">
          {/* Light mode side */}
          <div className={`flex items-center justify-center w-7 h-7 rounded-full transition-all duration-300 ${!isDark ? 'bg-heliona-mint text-heliona-navy shadow-md' : 'text-white/40'}`} data-id="element-990">
            <Sun className="w-3.5 h-3.5" data-id="element-991" />
          </div>

          {/* Dark mode side */}
          <div className={`flex items-center justify-center w-7 h-7 rounded-full transition-all duration-300 ${isDark ? 'bg-heliona-mint text-heliona-navy shadow-md' : 'text-heliona-charcoal/40'}`} data-id="element-992">
            <Moon className="w-3.5 h-3.5" data-id="element-993" />
          </div>
        </button>
      </motion.div>

      {/* Main Content Area */}
      <main className="relative w-full h-full flex items-center justify-center z-10" data-id="element-994">
        <AnimatePresence initial={false} custom={direction} mode="wait" data-id="element-995">
          <motion.div key={currentSlide} custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          x: {
            type: 'spring',
            stiffness: 300,
            damping: 30
          },
          opacity: {
            duration: 0.2
          }
        }} className="absolute w-full h-full" data-id="element-996">
            <CurrentSlideComponent isDark={isDark} data-id="element-997" />
          </motion.div>
        </AnimatePresence>
      </main>

      {/* Navigation Controls */}
      <div className="absolute inset-y-0 left-0 w-24 flex items-center justify-start pl-4 z-20 group" data-id="element-998">
        <button onClick={() => paginate(-1)} disabled={currentSlide === 0} className={`p-3 rounded-full transition-all disabled:opacity-0 disabled:cursor-not-allowed transform -translate-x-4 group-hover:translate-x-0 ${isDark ? 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white' : 'bg-heliona-charcoal/5 text-heliona-charcoal/50 hover:bg-heliona-charcoal/10 hover:text-heliona-charcoal'}`} data-id="element-999">
          <ChevronLeft className="w-8 h-8" data-id="element-1000" />
        </button>
      </div>

      <div className="absolute inset-y-0 right-0 w-24 flex items-center justify-end pr-4 z-20 group" data-id="element-1001">
        <button onClick={() => paginate(1)} disabled={currentSlide === slides.length - 1} className={`p-3 rounded-full transition-all disabled:opacity-0 disabled:cursor-not-allowed transform translate-x-4 group-hover:translate-x-0 ${isDark ? 'bg-white/5 text-white/50 hover:bg-white/10 hover:text-white' : 'bg-heliona-charcoal/5 text-heliona-charcoal/50 hover:bg-heliona-charcoal/10 hover:text-heliona-charcoal'}`} data-id="element-1002">
          <ChevronRight className="w-8 h-8" data-id="element-1003" />
        </button>
      </div>

      {/* Footer / Progress */}
      <footer className="absolute bottom-0 left-0 w-full p-8 flex justify-center items-center z-20" data-id="element-1004">
        <div className="flex gap-3" data-id="element-1005">
          {slides.map((_, index) => <button key={index} onClick={() => {
          setDirection(index > currentSlide ? 1 : -1);
          setCurrentSlide(index);
        }} className={`h-1.5 rounded-full transition-all duration-300 ${index === currentSlide ? 'w-8 bg-heliona-mint' : isDark ? 'w-2 bg-white/20 hover:bg-white/40' : 'w-2 bg-heliona-charcoal/20 hover:bg-heliona-charcoal/40'}`} aria-label={`Go to slide ${index + 1}`} data-id="element-1006" />)}
        </div>
      </footer>
    </div>;
}